import axios from "axios";

import { MessageBus } from "./message_bus.js";
import { Retry } from "./retry.js";
import { Utils } from "./utils.js";

export class PlaycocolaAPI {
  // The only place the two calls with credentials go. A link can point this page at any server, so the two API URLs
  // agreeing proves nothing, and the path counts as well as the origin: an uploaded game build is served from the
  // platform's own origin, with its open CORS, and could name a recording someone else opened.
  static PLATFORM_ORIGINS = [
    "https://app.playcocola.com",
    ...(import.meta.env.DEV ? ["http://localhost:3000", "https://app.playcocola.com.pizza"] : [])
  ];
  static PLATFORM_API_PATH = "/api/tester/";

  #playtestRoundApiUrl
  #apiToken
  playtestRound

  constructor({
    playtestRoundApiUrl,
    apiToken
  } = {}) {
    this.#playtestRoundApiUrl = playtestRoundApiUrl;
    this.#apiToken = apiToken;
  }

  // Three quick attempts, 1 s and then 2 s apart (A17). Throws the last error,
  // App.loadPlaytestRound() tells the tester.
  async intPlaytestRound() {
    const response = await Retry.withBackoff(
      () => this.requestPlaytestRoundInfo(),
      { attempts: 3, delay: (attempt) => 1_000 * (attempt + 1) }
    );

    this.setPlaytestRound(response.data);
  }

  setPlaytestRound(data) {
    const playtestRound = {};

    playtestRound.playSessionApiURL = data.play_session_api_url;
    playtestRound.uuid = data.playtest_round.uuid;
    playtestRound.title = data.playtest_round.title;
    playtestRound.description = data.playtest_round.description ?? "";
    playtestRound.playtesterExpectations = data.playtest_round.playtester_expectations ?? "";
    playtestRound.coverUrl = data.playtest_round.cover_url;
    playtestRound.playtestingSessionMinutes = data.playtest_round.playtesting_session_minutes;
    playtestRound.fullGameplayMinutes = data.playtest_round.full_gameplay_minutes;
    playtestRound.genres = data.playtest_round.genres ?? [];
    playtestRound.playtesterReward = data.playtest_round.playtester_reward ?? "";
    playtestRound.requiredCheckboxes = data.playtest_round.required_checkboxes ?? [];
    playtestRound.afterRequiredCheckboxesDescription = data.playtest_round.after_required_checkboxes_description;
    playtestRound.afterSessionFinishedDescription = data.playtest_round.after_session_finished_description;
    playtestRound.gameBuildUrl = data.playtest_round.game_build_url;
    playtestRound.isFinished = data.playtest_round.is_finished;
    playtestRound.finishAt = data.playtest_round.finish_at;
    // Both from 3.20.0's server: the developer's channels on a Bounty recording, where questions go otherwise
    playtestRound.developerContact = data.developer_contact ?? null;
    playtestRound.supportEmail = data.support_email ?? null;

    this.playtestRound = playtestRound;
  }

  // async requestPlaytestRoundInfoMock() {
  //   const data = {};
  //   data.playtest_round = {};

  //   data.play_session_api_url = "PLAY_SESSION_API_URL";
  //   data.playtest_round.uuid = "UUID";
  //   data.playtest_round.title = "TITLE";
  //   data.playtest_round.description = "DESCRIPTION";
  //   data.playtest_round.cover_url = null;
  //   data.playtest_round.playtesting_session_minutes = null;
  //   data.playtest_round.full_gameplay_minutes = null;
  //   data.playtest_round.genres = [];
  //   data.playtest_round.playtester_reward = null;
  //   data.playtest_round.required_checkboxes = [];
  //   data.playtest_round.after_required_checkboxes_description = null;
  //   data.playtest_round.after_session_finished_description = null;
  //   data.playtest_round.game_build_url = null;

  //   return data;
  // }

  // async requestPlaytestRoundInfoMock() {
  //   const data = {};
  //   data.playtest_round = {};

  //   data.play_session_api_url = "PLAY_SESSION_API_URL";
  //   data.playtest_round.uuid = "UUID";
  //   data.playtest_round.title = "TITLE";
  //   data.playtest_round.description = "DESCRIPTION";
  //   data.playtest_round.cover_url = "https://placehold.co/600x300";
  //   data.playtest_round.playtesting_session_minutes = 30;
  //   data.playtest_round.full_gameplay_minutes = 120;
  //   data.playtest_round.genres = ["genre_1", "genre_2"];
  //   data.playtest_round.playtester_reward = "PLAYTESTER_REWARD";
  //   data.playtest_round.required_checkboxes = ["check box **1**", "check box **3**"];
  //   data.playtest_round.after_required_checkboxes_description = "AFTER_REQUIRED_CHECKBOXES_DESCRIPTION";
  //   data.playtest_round.after_session_finished_description = "AFTER_SESSION_FINISHED_DESCRIPTION";
  //   data.playtest_round.game_build_url = "GAME_BUILD_URL";

  //   return data;
  // }

  requestPlaytestRoundInfo() {
    return axios.request({
        method: "get",
        url: this.#playtestRoundApiUrl,
        headers: this.#headers()
      });
  }

  // The page's own origin is the tests and the dev mock
  isPlatformApi() {
    return [this.#playtestRoundApiUrl, this.playtestRound.playSessionApiURL].every((url) => {
      try {
        const { origin, pathname } = new URL(url, window.location.href);

        return origin === window.location.origin ||
          (PlaycocolaAPI.PLATFORM_ORIGINS.includes(origin) && pathname.startsWith(PlaycocolaAPI.PLATFORM_API_PATH));
      } catch (error) {
        return false;
      }
    });
  }

  // A link from the recognition answer, only to the server that gave it. Null otherwise.
  platformUrl(url) {
    if (typeof url !== "string") {
      return null;
    }

    try {
      const origin = new URL(this.playtestRound.playSessionApiURL, window.location.href).origin;
      const parsed = new URL(url, origin);

      return parsed.origin === origin ? parsed.href : null;
    } catch (error) {
      return null;
    }
  }

  // Who is signed in on this browser. Only this call and requestRecordAsGuest() carry credentials, so a server without
  // them fails here and the tester stays a guest. 5 s, at most 2 attempts: no answer counts as a failed check. Throws.
  async requestRecognition() {
    const response = await Retry.withBackoff(
      () => axios.request({
        method: "post",
        url: this.playtestRound.playSessionApiURL + "/recognise",
        data: { return_url: window.location.href },
        headers: this.#headers(),
        withCredentials: true,
        timeout: 5_000
      }),
      { attempts: 2, delay: () => 1_000 }
    );

    return response.data;
  }

  // Retried like the uploads until the server answers: the recording stays on the account until it does. Throws on a
  // refusal, the only way out.
  async requestRecordAsGuest(guestToken) {
    await Retry.withBackoff(
      () => axios.request({
        method: "post",
        url: this.playtestRound.playSessionApiURL + "/record_as_guest",
        data: { guest_token: guestToken },
        headers: this.#headers(),
        withCredentials: true,
        timeout: 15_000
      })
    );
  }

  // Resolves when the server answered, or not at all: never throws
  sendDebugEvent(value) {
    if (!this.playtestRound) {
      console.log("sendDebugEvent() not sent, the Playtest Round is not loaded", value);
      return;
    }

    let formData = new FormData();
    formData.append("tag", "client-action");
    formData.append("value", value);

    return axios.request({
      method: "post",
      url: this.playtestRound.playSessionApiURL + "/event",
      data: formData,
      headers: this.#headers()
    }).catch((error) => {
      console.error("On sendDebugEvent()", error);
    });
  }

  async sendSystemInfo() {
    MessageBus.debug("updateSystemInfo :: ini");
    let uaParserData = Utils.getUserAgentData();
    let media_devices = await Utils.getMediaDevices()

    let data = {
      user_agent: uaParserData,
      media_devices: media_devices
    }

    try {
      await axios.request({
        method: "post",
        data: data,
        url: this.playtestRound.playSessionApiURL + "/update_system_info",
        headers: this.#headers()
      });

      MessageBus.debug("updateSystemInfo :: end");
    } catch (error) {
      // Telemetry: not worth interrupting the tester (A18)
      console.warn("updateSystemInfo :: error", error);
    }
  }

  // How the session is being recorded (RecordingManager.recordingInfo()), shown
  // to the developer. One retry after 3 s; an older server answers 404, which
  // is not retried. Never throws: the tester is never told.
  async sendRecordingInfo(info) {
    try {
      await Retry.withBackoff(
        () => axios.request({
          method: "post",
          url: this.playtestRound.playSessionApiURL + "/recording_info",
          data: { recording_info: info },
          headers: this.#headers(),
          timeout: 15_000
        }),
        { attempts: 2, delay: () => 3_000 }
      );
    } catch (error) {
      console.warn("sendRecordingInfo :: error", error);
    }
  }

  // part_index is extra: the server ignores unknown fields
  async uploadVideoPart(seq, date, blob, { signal = null, onProgress = null } = {}) {
    const filename = "video_part_" + date + ".webm";
    const formData = new FormData();
    formData.append("video_part", blob, filename);
    formData.append("part_index", seq);

    await axios.request({
      method: "post",
      url: this.playtestRound.playSessionApiURL + "/video_part",
      data: formData,
      headers: this.#headers(),
      signal: signal,
      onUploadProgress: (p) => {
        if (onProgress)
          onProgress(p.progress);
      }
    });
  }

  // One automatic retry after 3 s (B9)
  async sendFinalThoughts(name, email, comment) {
    let formData = new FormData();
    formData.append("user_name", name);
    formData.append("user_email", email);
    formData.append("user_comment", comment);

    await Retry.withBackoff(
      () => axios.request({
        method: "post",
        url: this.playtestRound.playSessionApiURL + "/comment",
        data: formData,
        headers: this.#headers()
      }),
      { attempts: 2, delay: () => 3_000 }
    );
  }

  // Retried with the uploads' backoff for about two minutes (B11). Resolves to
  // true when the server confirmed the end of the session, never throws.
  async sendSignalSessionFinalized() {
    MessageBus.debug("sendSignalSessionFinalized :: ini");

    try {
      await Retry.withBackoff(
        () => axios.request({
          method: "post",
          url: this.playtestRound.playSessionApiURL + "/session_finalized",
          headers: this.#headers()
        }),
        { deadlineMs: 120_000 }
      );

      MessageBus.debug("sendSignalSessionFinalized :: end");
      return true;
    } catch (error) {
      console.error("sendSignalSessionFinalized :: error", error);
      MessageBus.debug("sendSignalSessionFinalized :: failed: " + Utils.normalizeError(error).message);
      return false;
    }
  }

  // One automatic retry after 3 s (B9)
  async sendTimedComment(second_at_formatted, flair, comment) {
    MessageBus.debug("sendTimedComment :: ini");

    let formData = new FormData();
    formData.append("second_at_formatted", second_at_formatted);
    formData.append("flair", flair);
    formData.append("comment", comment);

    await Retry.withBackoff(
      () => axios.request({
        method: "post",
        url: this.playtestRound.playSessionApiURL + "/add_timed_comment",
        data: formData,
        headers: this.#headers()
      }),
      { attempts: 2, delay: () => 3_000 }
    );

    MessageBus.debug("sendTimedComment :: end");
  }

  #headers() {
    return { "Authorization": "Playcocola " + this.#apiToken };
  }
}
